import { useEffect, useState } from 'preact/hooks';
import type { PrActionBody, QueueBody, QueueItem } from '../../shared/pr/api';
import { ENTITIES } from '../../shared/rules/numbering';
import { StatusPill } from '../components/StatusPill';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDateTime } from '../lib/dates';
import { StatusGuide } from '../components/StatusGuide';
import { personName } from '../lib/people';

// Procurement queue (screen 07, P2.1): every open request, its stage, owner and working-day age.
// The broker filters on indexed columns and pages; this screen only chooses the filters.

type View = 'all' | 'unassigned' | 'mine' | 'pastTarget';
type Sort = 'newest' | 'oldest' | 'urgent';
const SORTS: readonly Sort[] = ['newest', 'oldest', 'urgent'];
const STAGES = [
  'ProcurementReview',
  'WithDeptHead',
  'Sourcing',
  'PODraft',
  'PettyCash',
  'OnHold',
  'PendingCFO',
  'PendingVertical',
  'POSentPendingCFO',
  'POIssued',
  'PartlyReceived',
  'Received',
  'Invoiced',
  'VoucherWithFinance',
  'Paid',
  'Returned',
] as const;
const ASSIGNABLE = new Set<string>(STAGES.slice(2).filter((s) => s !== 'Returned'));

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; reference: string | null; forbidden: boolean }
  | { kind: 'ok'; data: QueueBody };

const money = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function QueuePage() {
  const { t, lang } = useI18n();
  const [view, setView] = useState<View>('all');
  const [entity, setEntity] = useState('');
  const [office, setOffice] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<Sort>('newest');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);

  // Search as you type, once typing pauses (D119); the broker searches every open request.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    let live = true;
    const q = new URLSearchParams({
      view,
      sort,
      page: String(page),
      ...(search ? { q: search } : {}),
      ...(entity ? { entity } : {}),
      ...(office ? { office } : {}),
      ...(category ? { category } : {}),
      ...(status ? { status } : {}),
    });
    apiGet<QueueBody>(`/api/queue?${q.toString()}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'error',
          reference: e?.correlationId ?? null,
          forbidden: e?.code === 'forbidden',
        });
      });
    return () => {
      live = false;
    };
  }, [view, entity, office, category, status, sort, search, page, reload]);

  const filter =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
      setNotice(null);
    };

  async function assign(item: QueueItem) {
    try {
      await apiSend<PrActionBody>('POST', `/api/prs/${encodeURIComponent(item.prNo)}/action`, {
        action: 'assign',
        etag: item.etag,
      });
      setNotice(t('queue.assigned', { no: item.prNo }));
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNotice(
        t(e?.code === 'self_approval' ? 'queue.error.own' : 'review.error.generic') +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    }
  }

  const data = load.kind === 'ok' ? load.data : null;
  const tiles: { key: string; label: string; value: number; warn?: boolean }[] = data
    ? [
        { key: 'review', label: t('queue.tile.toReview'), value: data.counts.toReview },
        { key: 'sourcing', label: t('queue.tile.sourcing'), value: data.counts.sourcing },
        {
          key: 'late',
          label: t('queue.tile.pastTarget', { n: String(data.slaDays) }),
          value: data.counts.pastTarget,
          warn: data.counts.pastTarget > 0,
        },
        { key: 'rush', label: t('queue.tile.rush'), value: data.counts.rush },
      ]
    : [];

  return (
    <div class="stack">
      <p class="caption">{t('queue.intro')}</p>
      {data && (
        <ul class="tiles" aria-label={t('home.tiles')}>
          {tiles.map((tile) => (
            <li key={tile.key} class={`tile${tile.warn ? ' tile--warning' : ''}`}>
              <span class="caption">{tile.label}</span>
              <span class="tile__value">{tile.value}</span>
            </li>
          ))}
        </ul>
      )}
      <div class="review-bar">
        <div class="admin-tabs" role="group" aria-label={t('queue.views')}>
          {(['all', 'unassigned', 'mine', 'pastTarget'] as const).map((v) => (
            <button
              key={v}
              type="button"
              class="chip"
              aria-pressed={v === view}
              onClick={() => filter(setView)(v)}
            >
              {t(`queue.view.${v}` as MessageKey)}
            </button>
          ))}
        </div>
        <div class="queue-filters">
          <div class="field review-search">
            <label for="q-search">{t('review.search')}</label>
            <input
              id="q-search"
              type="search"
              value={query}
              maxLength={100}
              placeholder={t('queue.searchHint')}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label for="q-entity">{t('pr.profile.entity')}</label>
            <select
              id="q-entity"
              value={entity}
              onChange={(e) => filter(setEntity)((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('queue.all')}</option>
              {ENTITIES.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="q-office">{t('review.office')}</label>
            <select
              id="q-office"
              value={office}
              onChange={(e) => filter(setOffice)((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('queue.all')}</option>
              {(data?.offices ?? []).map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="q-category">{t('req.f.category')}</label>
            <select
              id="q-category"
              value={category}
              onChange={(e) => filter(setCategory)((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('queue.all')}</option>
              {(data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="q-stage">{t('queue.stage')}</label>
            <select
              id="q-stage"
              value={status}
              onChange={(e) => filter(setStatus)((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('queue.all')}</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {t(`status.${s}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="q-sort">{t('queue.sort')}</label>
            <select
              id="q-sort"
              value={sort}
              onChange={(e) => filter(setSort)((e.target as HTMLSelectElement).value as Sort)}
            >
              {SORTS.map((x) => (
                <option key={x} value={x}>
                  {t(`queue.sort.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {notice && (
        <p class="form-saved" role="status">
          {notice}
        </p>
      )}
      <section class="card" aria-labelledby="queue-title">
        <h2 id="queue-title" class="visually-hidden">
          {t('nav.queue')}
        </h2>
        {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p>{t(load.forbidden ? 'review.error.forbidden' : 'req.loadError')}</p>
            {load.reference && (
              <p class="caption" dir="ltr">
                {t('error.reference', { ref: load.reference })}
              </p>
            )}
          </div>
        )}
        {data &&
          (data.items.length === 0 ? (
            <p>{search ? t('review.noMatch') : t('review.empty')}</p>
          ) : (
            <>
              <div class="table-wrap">
                <table class="table table--zebra">
                  <thead>
                    <tr>
                      <th scope="col">{t('queue.col.request')}</th>
                      <th scope="col">{t('queue.col.raised')}</th>
                      <th scope="col">{t('queue.col.requester')}</th>
                      <th scope="col">{t('queue.stage')}</th>
                      <th scope="col" class="num">
                        {t('queue.col.value')}
                      </th>
                      <th scope="col" class="num">
                        {t('queue.col.age')}
                      </th>
                      <th scope="col">{t('queue.col.owner')}</th>
                      <th scope="col">
                        <span class="visually-hidden">{t('admin.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((i) => (
                      <tr key={i.prNo}>
                        <td>
                          <a class="row-ref" href={`#/requests/${i.prNo}`} dir="ltr">
                            {i.prNo}
                          </a>
                          <span class="cell-sub" dir="auto">
                            {i.title}
                          </span>
                        </td>
                        <td class="nowrap" dir="ltr">
                          {i.created ? formatDateTime(i.created, lang) : '—'}
                        </td>
                        <td>
                          {i.requesterName || '—'}
                          <span class="caption cell-sub">
                            {i.department} ·{' '}
                            <span class="ref" dir="ltr">
                              {i.entity}
                            </span>
                          </span>
                        </td>
                        <td>
                          <StatusPill status={i.status} />{' '}
                          {i.rush && <span class="pill pill--danger">{t('req.rush')}</span>}
                          {i.unsignedOverdue && (
                            <span class="pill pill--danger">{t('queue.unsignedOverdue')}</span>
                          )}
                        </td>
                        <td class="num" dir="ltr">
                          {i.estBudget === null
                            ? '—'
                            : `${i.currency} ${money.format(i.estBudget)}`}
                        </td>
                        <td class="num" dir="ltr">
                          <span class={i.pastTarget ? 'pill pill--warning' : ''}>
                            {t('queue.age', { n: String(i.age) })}
                          </span>
                        </td>
                        <td>
                          {i.owner ? <span title={i.owner}>{personName(i.owner)}</span> : '—'}
                        </td>
                        <td>
                          {i.status === 'ProcurementReview' || i.status === 'WithDeptHead' ? (
                            <a
                              class="button button--small"
                              href={`#/review/${encodeURIComponent(i.prNo)}`}
                            >
                              {t('queue.review')}
                            </a>
                          ) : (
                            ASSIGNABLE.has(i.status) &&
                            !i.own &&
                            !i.mine && (
                              <button
                                type="button"
                                class="button button--small"
                                onClick={() => void assign(i)}
                                aria-label={t('queue.assignRow', { no: i.prNo })}
                              >
                                {t('queue.assign')}
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <nav class="pager" aria-label={t('queue.pages')}>
                <span class="caption">
                  {t('queue.range', {
                    from: String((data.page - 1) * 50 + 1),
                    to: String((data.page - 1) * 50 + data.items.length),
                    total: String(data.total),
                  })}
                </span>
                {data.pages > 1 && (
                  <>
                    <button
                      type="button"
                      class="button button--small"
                      disabled={data.page <= 1}
                      onClick={() => setPage(data.page - 1)}
                    >
                      {t('queue.prev')}
                    </button>
                    <button
                      type="button"
                      class="button button--small"
                      disabled={data.page >= data.pages}
                      onClick={() => setPage(data.page + 1)}
                    >
                      {t('queue.next')}
                    </button>
                  </>
                )}
              </nav>
            </>
          ))}
      </section>
      <StatusGuide staff />
    </div>
  );
}
